import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Sparkles, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const NONE = "__none__";

const TechPicker = ({ techs, picked, setPicked }) => {
  const [q, setQ] = useState("");
  const list = techs.filter((t) => t.label.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  return (
    <div className="border bg-white p-4" data-testid="plan-tech-picker">
      <Label className="micro-label">Tecnici disponibili per questo file ({picked.length} scelti)</Label>
      <Input className="mt-2 h-9" placeholder="Cerca tecnico…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="plan-tech-search" />
      <div className="mt-2 grid max-h-56 gap-1 overflow-y-auto sm:grid-cols-2">
        {list.map((t) => (
          <label key={t.id} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted/60">
            <Checkbox checked={picked.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`plan-tech-${t.id}`} />{t.label}
          </label>
        ))}
      </div>
    </div>
  );
};

const PlanRow = ({ r, techs, update, warn }) => (
  <TableRow data-testid={`plan-row-${r.external_order_id}`} className={!r.employee_id ? "bg-amber-50/50" : warn ? "bg-red-50/60" : ""}>
    <TableCell className="font-mono text-sm">{r.external_order_id}</TableCell>
    <TableCell className="text-sm">{r.street}<div className="text-xs text-muted-foreground">{r.city}</div></TableCell>
    <TableCell className="font-mono text-sm">{r.appointment_time || "—"}</TableCell>
    <TableCell>
      <Select value={r.employee_id || NONE} onValueChange={(v) => update({ employee_id: v === NONE ? null : v, note: "" })}>
        <SelectTrigger className="h-8 w-48" data-testid={`plan-row-tech-${r.external_order_id}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value={NONE}>— Da concordare —</SelectItem>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
    </TableCell>
    <TableCell><Input type="date" className="h-8 w-36" value={r.scheduled_date} onChange={(e) => update({ scheduled_date: e.target.value })} data-testid={`plan-row-date-${r.external_order_id}`} /></TableCell>
    <TableCell>
      <Select value={r.slot} onValueChange={(v) => update({ slot: v })}>
        <SelectTrigger className="h-8 w-32" data-testid={`plan-row-slot-${r.external_order_id}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="mattina">Mattina</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem><SelectItem value="sera">Sera</SelectItem></SelectContent>
      </Select>
    </TableCell>
    <TableCell className="text-xs text-amber-800">{r.note}{warn && <span className="block text-red-700" data-testid={`plan-row-warn-${r.external_order_id}`}>{warn}</span>}</TableCell>
  </TableRow>
);

const Summary = ({ rows, techs }) => {
  const counts = techs.map((t) => [t.label, rows.filter((r) => r.employee_id === t.id).length]).filter(([, n]) => n);
  const open = rows.filter((r) => !r.employee_id).length;
  return (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="plan-summary">
      {counts.map(([l, n]) => <span key={l} className="border bg-white px-2 py-1"><b className="font-mono">{n}</b> · {l}</span>)}
      {open > 0 && <span className="border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800" data-testid="plan-open-count"><b className="font-mono">{open}</b> da concordare</span>}
    </div>
  );
};

export const PlanStep = ({ batch, onDone }) => {
  const { teams, employees } = useData();
  const techs = useMemo(() => {
    const ids = new Set(teams.filter((t) => !t.archived).flatMap((t) => t.member_ids));
    return employees.filter((e) => ids.has(e.id) && e.active !== false).map((e) => ({ id: e.id, label: `${e.last_name} ${e.first_name}` })).sort((a, b) => a.label.localeCompare(b.label));
  }, [teams, employees]);
  const [picked, setPicked] = useState([]);
  const [perTech, setPerTech] = useState(4);
  const [rows, setRows] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);

  const propose = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${batch.id}/plan-proposal`, { employee_ids: picked, per_tech: Number(perTech) || 4 });
      setRows(data.items);
      setWarnings(data.warnings);
      if (!data.items.length) toast.info("Nessuna pratica da pianificare in questo import");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    const items = rows.filter((r) => r.employee_id).map(({ order_id, employee_id, scheduled_date, slot }) => ({ order_id, employee_id, scheduled_date, slot }));
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${batch.id}/plan`, { items });
      toast.success(`${data.planned} pratiche pianificate`);
      data.errors.forEach((e) => toast.error(e.error));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const update = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const warnOf = (r) => {
    if (!r.employee_id) return "";
    const same = rows.filter((x) => x.employee_id === r.employee_id && x.scheduled_date === r.scheduled_date);
    if (r.appointment_time && same.some((x) => x !== r && x.appointment_time === r.appointment_time)) return `Stesso tecnico già impegnato alle ${r.appointment_time}`;
    return same.length > (Number(perTech) || 4) ? `Oltre ${perTech} impianti nel giorno per questo tecnico` : "";
  };
  const pickedTechs = techs.filter((t) => picked.includes(t.id) || rows?.some((r) => r.employee_id === t.id));

  return (
    <div className="space-y-6" data-testid="plan-step">
      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <TechPicker techs={techs} picked={picked} setPicked={setPicked} />
        <div className="space-y-3 border bg-white p-4">
          <Label className="micro-label">Impianti medi per tecnico</Label>
          <Input type="number" min="1" max="12" value={perTech} onChange={(e) => setPerTech(e.target.value)} data-testid="plan-per-tech-input" />
          <Button className="w-full" disabled={!picked.length || busy} onClick={propose} data-testid="plan-propose-button"><Sparkles className="mr-1 h-4 w-4" />{rows ? "Ricalcola proposta" : "Proponi pianificazione"}</Button>
          <p className="text-xs text-muted-foreground">Raggruppo impianti vicini, un solo impianto per orario a tecnico. Se troppi impianti cadono allo stesso orario restano «da concordare» con il cliente.</p>
        </div>
      </div>
      {warnings.map((w) => <div key={w} className="flex items-center gap-2 border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="plan-warning"><AlertTriangle className="h-4 w-4" />{w}</div>)}
      {rows && rows.length > 0 && (
        <>
          <Summary rows={rows} techs={pickedTechs} />
          <div className="overflow-x-auto border bg-white" data-testid="plan-table">
            <Table>
              <TableHeader><TableRow className="bg-muted/50"><TableHead>Pratica</TableHead><TableHead>Indirizzo</TableHead><TableHead>Ora</TableHead><TableHead>Tecnico</TableHead><TableHead>Giorno</TableHead><TableHead>Fascia</TableHead><TableHead>Nota</TableHead></TableRow></TableHeader>
              <TableBody>{rows.map((r, i) => <PlanRow key={r.order_id} r={r} techs={pickedTechs.length ? pickedTechs : techs} update={(p) => update(i, p)} warn={warnOf(r)} />)}</TableBody>
            </Table>
          </div>
        </>
      )}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onDone} data-testid="plan-skip-button">Pianifica dopo dal planning</Button>
        {rows && rows.length > 0 && <Button onClick={confirm} disabled={busy || !rows.some((r) => r.employee_id)} data-testid="plan-confirm-button"><CalendarCheck className="mr-1 h-4 w-4" />Conferma pianificazione ({rows.filter((r) => r.employee_id).length})</Button>}
      </div>
    </div>
  );
};
